import { Either, fold, right } from "fp-ts/lib/Either";
import * as t from "io-ts";
import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import { _Error } from "@/lib/wordpress/data";
import {
  AISettingsV,
  ChatMessage,
  CompletionFormat,
  UnsplashImage,
  UnsplashImageV
} from "./types";
import { fpJsonStringify } from "@/util/fp-json-stringify";
import fpJsonParse from "@/util/fp-json-parse";
import { pipe } from "fp-ts/lib/function";
import { globalStore } from "@/store/store";
import { breakdanceUndo } from "../undo/undo";
import jsonAutocomplete from "json-autocomplete";
import { callAIBatch } from "@/futurelayer/api/api";

export async function requestCompletion(
  messages: ChatMessage[],
  format: CompletionFormat = "json",
  action = "breakdance_ai_request_completion",
  useRailwayServer: boolean,
  abortController?: AbortController
): Promise<Either<_Error[], string>> {

  if (useRailwayServer) {
      return requestCompletionFromRailwayServer(messages, format, abortController);
  }

  return makeWordPressAjaxRequest({
    action,
    friendlyName: "AI Assistant",
    dataToPost: {
      messages: fpJsonStringify(messages),
      format: format
    },
    decoder: t.string,
    resultKey: "data",
    abortController: abortController
  });
  
}

export async function requestCompletionFromRailwayServer(
  messages: ChatMessage[],
  format: CompletionFormat = "json",
  abortController?: AbortController
): Promise<Either<_Error[], string>> {
  try {
    let responseFormat: any = undefined;
    
    if (format === "json") {
      responseFormat = { type: "json_object" };
    }
    
    const batchRequest = {
      id: "completion-request",
      messages,
      responseFormat
    };
    
    const batchResults = await callAIBatch(
      [batchRequest],
      abortController!
    );

    if (batchResults.length > 0 && batchResults[0].content) {
      return right(batchResults[0].content);
    }

    return right("this should not be reached");
  } catch (error) {
    console.error("Railway completion request failed:", error);
    return right("");
  }
}

export async function persistMessages(
  messages: ChatMessage[],
  format: CompletionFormat = "json",
  temperature: number
) {
  await makeWordPressAjaxRequest({
    action: "breakdance_ai_persist_messages",
    friendlyName: "AI Assistant - Persist Messages",
    dataToPost: {
      messages: fpJsonStringify(messages),
      format,
      temperature
    },
    decoder: t.type({ status: t.literal("ok") })
  });
}

export function getEventSource(endpoint = "/ai/stream-completion") {
  const url = `${window.Breakdance.restUrl}${endpoint}`;
  const separator = url.includes("?") ? "&" : "?";
  return new EventSource(
    `${url}${separator}_wpnonce=${window.Breakdance.restNonce}`
  );
}

export function getTextFromChunk(chunk: string) {
  const json = pipe(
    chunk,
    fpJsonParse,
    fold(
      () => {},
      x => x as any
    )
  );

  const text = json?.choices[0].delta.content;

  if (text !== null && text !== undefined) {
    return text;
  }

  return "";
}

// Clean the JSON string and mutate the original string.
export function cleanJson(json: string) {
  // Remove code block markdown, this is only needed if JSON Mode is enabled.
  json = json.replace("```json", "");
  json = json.replace(/^json/g, "");
  json = json.replace("```", "");
  return json;
}

// Clean the JSON string but don't mutate the original string.
export function cleanJsonBeforeParse(json: string) {
  // const lastDoubleQuote = json.trim().lastIndexOf('"');
  // const lastSimpleQuote = json.trim().lastIndexOf("'");
  // if (lastSimpleQuote > lastDoubleQuote) {
  //   // When the JSON string ends with a text containing a simple quote, without an ending double-quote jsonrepeair fail...
  //   json = `${json}"`;
  // }
  //
  // const lastComma = json.trim().lastIndexOf(",");
  // if (lastComma > lastDoubleQuote + 1) {
  //   // The +1 is to avoid to catch a property separator comma `", "`
  //   // When the JSON string ends with a text containing a comma, without an ending double-quote jsonrepeair explodes.
  //   json = `${json}"`;
  // }
  json = json.replace('"index" 0', '"index": 0'); // Common error.
  json = json.replace('index" 0', '"index": 0'); // Common error.
  json = json.replace('"index 0', '"index": 0'); // Common error.

  return jsonAutocomplete(json);
}

export async function getSuggestedPrompts(prompt: string) {
  return makeWordPressAjaxRequest({
    action: "breakdance_ai_get_suggested_prompts",
    friendlyName: "AI Assistant",
    dataToPost: {
      prompt
    },
    decoder: t.type({
      prompts: t.array(t.string)
    }),
    resultKey: "data"
  });
}

export async function talkToUnsplash(
  keywords: string[],
  limit: number,
  orientation?: "landscape" | "portrait" | "squarish" | null
): Promise<UnsplashImage[]> {
  const request = await makeWordPressAjaxRequest({
    action: "breakdance_ai_talk_to_unsplash",
    friendlyName: "Unsplash Assistant",
    dataToPost: {
      prompt: keywords.join(", "),
      limit,
      orientation
    },
    decoder: t.array(UnsplashImageV),
    resultKey: "data"
  });

  return pipe(
    request,
    fold(
      () => [],
      response => response
    )
  );
}

export async function fetchAiSettings() {
  const settings = await makeWordPressAjaxRequest({
    action: "breakdance_ai_get_settings",
    decoder: AISettingsV,
    resultKey: "data",
    suppressErrorLogging: true
  });

  return pipe(
    settings,
    fold(
      () => false,
      loadedData => {
        breakdanceUndo.ignore(() => {
          globalStore.setAiSettings(loadedData);
          globalStore.markCurrentAiSettingsAsUntouched();
        });
        return true;
      }
    )
  );
}

export async function saveAiSettings() {
  const settings = globalStore.aiSettings;

  const request = await makeWordPressAjaxRequest({
    action: "breakdance_ai_save_settings",
    dataToPost: {
      settings: fpJsonStringify(settings.prompt)
    },
    decoder: AISettingsV,
    resultKey: "data"
  });

  return pipe(
    request,
    fold(
      () => false,
      loadedData => {
        breakdanceUndo.ignore(() => {
          globalStore.setAiSettings(loadedData);
          globalStore.markCurrentAiSettingsAsUntouched();
        });
        return true;
      }
    )
  );
}
